/**
 * Admin-Tab „Handlungshinweise" (Spec 2026-09-16, §3.6): feste Matrix Art × Sprache.
 * Eigener Tab neben „Benachrichtigungen", weil die Texte in Mails UND auf der
 * Projektseite erscheinen. Muster wie der Vorlagen-Editor: Speichern + Zuruecksetzen,
 * kein Anlegen/Loeschen von Arten.
 */
import type { NotificationLocale } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/toast.js';
import { type ActionTextInput, type ActionTextListItem, api } from '../lib/api.js';

const QUERY_KEY = ['notif-action-texts'];
const LOCALES: NotificationLocale[] = ['de', 'en'];

export function ActionTextsAdmin() {
  const { t } = useTranslation();
  const [locale, setLocale] = useState<NotificationLocale>('de');
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: api.listActionTexts });
  const items = (query.data?.items ?? []).filter((i) => i.locale === locale);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.actions_title', 'Handlungshinweise')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t('admin.actions_subtitle', {
            due_meaning: '{{due_meaning}}',
            action: '{{action}}',
            defaultValue:
              'Was in Tagesübersicht und Projektseite als Handlung steht — je Bedeutung des Fälligkeitsdatums. Erscheint als {{due_meaning}} und {{action}} in den Mail-Vorlagen.',
          })}
        </p>
      </div>
      <label className="flex flex-col gap-1 text-xs text-[color:var(--ink-3)] w-fit">
        {t('admin.notifications_locale_label', 'Sprache')}
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value as NotificationLocale)}
          className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
        >
          {LOCALES.map((l) => (
            <option key={l} value={l}>
              {l.toUpperCase()}
            </option>
          ))}
        </select>
      </label>
      {query.isLoading && (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.loading', 'Lade…')}</p>
      )}
      <div className="space-y-3">
        {items.map((item) => (
          <ActionTextRow key={`${item.kind}:${item.locale}`} item={item} />
        ))}
      </div>
    </section>
  );
}

function ActionTextRow({ item }: { item: ActionTextListItem }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isHandover = item.kind === 'handover';
  const [draft, setDraft] = useState<ActionTextInput>({
    label: item.label,
    action_overdue: item.action_overdue,
    action_due_soon: item.action_due_soon,
  });
  useEffect(() => {
    setDraft({
      label: item.label,
      action_overdue: item.action_overdue,
      action_due_soon: item.action_due_soon,
    });
  }, [item.label, item.action_overdue, item.action_due_soon]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const save = useMutation({
    mutationFn: () => api.updateActionText(item.kind, item.locale, draft),
    onSuccess: () => {
      toast.success(t('admin.actions_saved', 'Handlungshinweis gespeichert'));
      invalidate();
    },
    onError: (err) =>
      toast.error(
        t('admin.actions_save_failed', 'Speichern fehlgeschlagen'),
        (err as Error).message,
      ),
  });
  const reset = useMutation({
    mutationFn: () => api.resetActionText(item.kind, item.locale),
    onSuccess: () => {
      toast.success(t('admin.actions_reset_done', 'Auf Default zurückgesetzt'));
      invalidate();
    },
    onError: (err) =>
      toast.error(
        t('admin.actions_reset_failed', 'Zurücksetzen fehlgeschlagen'),
        (err as Error).message,
      ),
  });

  const complete = isHandover
    ? !!draft.action_overdue?.trim()
    : !!draft.label?.trim() && !!draft.action_overdue?.trim() && !!draft.action_due_soon?.trim();

  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-[color:var(--ink)]">
          {t(`admin.actions_kind_${item.kind}`)}
        </span>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
            item.is_overridden
              ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
              : 'bg-[var(--surface-3)] text-[color:var(--ink-3)]'
          }`}
        >
          {item.is_overridden
            ? t('admin.notifications_status_custom', 'angepasst')
            : t('admin.notifications_status_default', 'Default')}
        </span>
      </div>
      {!isHandover && (
        <Field
          label={t('admin.actions_field_label', {
            due_meaning: '{{due_meaning}}',
            defaultValue: 'Bezeichnung ({{due_meaning}})',
          })}
          value={draft.label ?? ''}
          onChange={(v) => setDraft({ ...draft, label: v })}
        />
      )}
      <Field
        label={
          isHandover
            ? t('admin.actions_field_handover', 'Handlung')
            : t('admin.actions_field_overdue', 'Handlung, wenn das Datum überschritten ist')
        }
        value={draft.action_overdue ?? ''}
        onChange={(v) => setDraft({ ...draft, action_overdue: v })}
      />
      {!isHandover && (
        <Field
          label={t('admin.actions_field_due_soon', 'Handlung, wenn das Datum ansteht')}
          value={draft.action_due_soon ?? ''}
          onChange={(v) => setDraft({ ...draft, action_due_soon: v })}
        />
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          disabled={save.isPending || !complete}
          onClick={() => save.mutate()}
          className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('admin.notifications_save', 'Speichern')}
        </button>
        <button
          type="button"
          disabled={reset.isPending || !item.is_overridden}
          onClick={() => reset.mutate()}
          className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--ink-3)] disabled:opacity-40"
        >
          {t('admin.notifications_reset', 'Zurücksetzen')}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-xs text-[color:var(--ink-3)]">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 block w-full rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
      />
    </label>
  );
}
